<template>
  <div>
    <b-overlay :show="loadingDropdowns" rounded="sm">
      <b-form
        @submit.stop.prevent="onSubmit"
        :class="{
          'p-3 box-shadow2 rounded': !isDarkMode,
          'dark-div p-3 box-shadow2 rounded': isDarkMode
        }"
      >
        <b-row>
          <b-col md="6">
            <b-form-group label="Employee Code" label-for="qms-employee-code">
              <template #label><span>Employee Code</span> <span class="text-danger">*</span></template>
              <b-form-input
                id="qms-employee-code"
                v-model="form.employeeCode"
                :required="!isPayrollClosureOnlyMode"
                :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit || isQmsUserIdentityLocked"
                :class="inputClass"
                trim
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Store Id" label-for="qms-store">
              <template #label><span>Store Id</span> <span class="text-danger">*</span></template>
              <multiselect
                v-if="storeOptions.length"
                id="qms-store"
                :class="['box-shadow2', { 'qms-store-select-dark': isDarkMode }]"
                :options="storeOptions"
                v-model="storeIdSelect"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :searchable="true"
                :allow-empty="false"
                :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit || isQmsUserIdentityLocked"
                label="text"
                track-by="value"
                placeholder="Select Store Id"
              ></multiselect>
              <b-form-input
                v-else
                id="qms-store"
                v-model="form.storeCode"
                :required="!isPayrollClosureOnlyMode"
                :class="inputClass"
                trim
                placeholder="Store list loading…"
                :disabled="loading || isPayrollClosureOnlyMode || isGuestReadOnlyEdit || isQmsUserIdentityLocked"
              />
              <b-form-text v-if="storeOptions.length" id="qms-store-help" :class="isDarkMode ? 'text-light' : ''">
                {{ isQmsUserIdentityLocked ? 'Store id is auto-filled from your QMS profile.' : 'Select a store id from the list.' }}
              </b-form-text>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label-for="qms-name">
              <template #label><span>Employee Name</span> <span class="text-danger">*</span></template>
              <b-form-input
                id="qms-name"
                v-model="form.employeeName"
                :required="!isPayrollClosureOnlyMode"
                :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit || isQmsUserIdentityLocked"
                :class="inputClass"
                trim
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label-for="qms-dept">
              <template #label><span>Department</span> <span class="text-danger">*</span></template>
              <b-form-select
                v-if="departmentSelectOptions.length"
                id="qms-dept"
                v-model="form.department"
                :options="departmentSelectOptions"
                :class="inputClass"
                :required="!isPayrollClosureOnlyMode"
                :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit"
                @change="onDepartmentChange"
              >
                <template #first>
                  <b-form-select-option :value="''" disabled>Select department</b-form-select-option>
                </template>
              </b-form-select>
              <b-form-input
                v-else
                id="qms-dept"
                v-model="form.department"
                :required="!isPayrollClosureOnlyMode"
                :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit"
                :class="inputClass"
                trim
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label-for="qms-qtype">
              <template #label><span>Query Type</span> <span class="text-danger">*</span></template>
              <b-input-group class="box-shadow2">
                <b-form-select
                  v-if="queryTypeSelectOptions.length"
                  id="qms-qtype"
                  v-model="form.queryType"
                  :options="queryTypeSelectOptions"
                  :class="inputClass"
                  :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit || !form.department"
                  :required="!isPayrollClosureOnlyMode"
                >
                  <template #first>
                    <b-form-select-option :value="''" disabled>Select query type</b-form-select-option>
                  </template>
                </b-form-select>
                <b-form-input
                  v-else
                  id="qms-qtype"
                  v-model="form.queryType"
                  :required="!isPayrollClosureOnlyMode"
                  :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit"
                  :class="inputClass"
                  trim
                  placeholder="Type query type, or use + to configure options"
                />
                <b-input-group-append v-if="!isQmsOtpGuestSession && !isPayrollClosureOnlyMode">
                  <b-button
                    type="button"
                    :variant="isDarkMode ? 'outline-light' : 'outline-secondary'"
                    title="Open Dropdowns page"
                    class="d-flex align-items-center box-shadow2"
                    @click="goToDropdownsPage"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label-for="qms-mobile-number">
              <template #label><span>Mobile Number</span> <span class="text-danger">*</span></template>
              <b-form-input
                id="qms-mobile-number"
                v-model="form.employeeMobile"
                :required="!isPayrollClosureOnlyMode"
                :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit || isQmsUserIdentityLocked"
                :class="inputClass"
                trim
                maxlength="10"
                placeholder="Enter mobile number"
              />
              <b-form-text :class="isDarkMode ? 'text-light' : 'text-muted'">
                {{ isQmsUserIdentityLocked ? 'Mobile number is locked from your verified QMS profile.' : 'Enter the mobile number linked to your QMS profile.' }}
              </b-form-text>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label-for="qms-desc">
          <template #label><span>Query Description</span> <span class="text-danger">*</span></template>
          <b-form-textarea
            id="qms-desc"
            v-model="form.queryDescription"
            rows="5"
            :required="!isPayrollClosureOnlyMode"
            :disabled="isPayrollClosureOnlyMode || isGuestReadOnlyEdit"
            :class="textareaClass"
          />
        </b-form-group>
        <b-form-group
          v-if="showEmployeeRefImagesSection"
          id="group-qms-employee-ref-images"
          label-for="input-qms-employee-ref-images"
        >
          <template v-slot:label>Employee Attachments</template>
          <input
            v-if="canUploadEmployeeRefImages"
            type="file"
            multiple
            :accept="attachmentAccept"
            @change="selectEmployeeRefFile($event)"
          />
          <b-form-text v-if="canUploadEmployeeRefImages" :class="isDarkMode ? 'text-light' : 'text-muted'">
            Images and PDF files are allowed. Files upload when you submit the form.
          </b-form-text>
        </b-form-group>
        <div
          v-if="showEmployeeRefImagesSection && (savedEmployeeRefImages.length || pendingEmployeeRefImages.length)"
          class="d-flex flex-wrap mt-2"
        >
          <div
            v-for="url in savedEmployeeRefImages"
            :key="`saved-employee-${url}`"
            class="qms-attachment-preview mr-2 mb-2"
          >
<img
  v-if="isImageAttachmentUrl(url)"
  :src="url"
  alt="Employee attachment"
  class="qms-clickable-image"
  @click="openImagePreview(url)"
/>
            <div v-else class="qms-attachment-file" :class="{ 'qms-attachment-file--dark': isDarkMode }">
              <font-awesome-icon :icon="['fas', 'file-pdf']" class="text-danger mr-1" />
              <span class="qms-attachment-file__name">{{ getAttachmentFileName(url) }}</span>
            </div>
            <b-button
              type="button"
              size="sm"
              variant="outline-primary"
              class="qms-attachment-preview__download box-shadow2"
              @click="downloadAttachment(url)"
            >
              <font-awesome-icon :icon="['fas', 'download']" />
            </b-button>
          </div>
          <div
            v-for="item in pendingEmployeeRefImages"
            :key="item.id"
            class="qms-attachment-preview mr-2 mb-2"
          >
<img
  v-if="isImageFile(item.file)"
  :src="item.preview"
  :alt="item.file.name"
  class="qms-clickable-image"
  @click="openImagePreview(item.preview)"
/>
            <div v-else class="qms-attachment-file" :class="{ 'qms-attachment-file--dark': isDarkMode }">
              <font-awesome-icon :icon="['fas', 'file-pdf']" class="text-danger mr-1" />
              <span class="qms-attachment-file__name">{{ item.file.name }}</span>
            </div>
            <b-button
              type="button"
              size="sm"
              variant="outline-primary"
              class="qms-attachment-preview__download box-shadow2"
              @click="downloadPendingAttachment(item)"
            >
              <font-awesome-icon :icon="['fas', 'download']" />
            </b-button>
            <b-button
              v-if="canUploadEmployeeRefImages"
              type="button"
              size="sm"
              variant="danger"
              class="qms-attachment-preview__remove box-shadow2"
              @click="removePendingEmployeeRefFile(item.id)"
            >
              <font-awesome-icon :icon="['fas', 'trash']" />
            </b-button>
          </div>
        </div>
        <b-form-group
          v-if="showClosureRefImagesSection"
          id="group-qms-closure-ref-images"
          label-for="input-qms-closure-ref-images"
        >
          <template v-slot:label>Role Attachments</template>
          <input
            v-if="canUploadClosureRefImages"
            type="file"
            multiple
            :accept="attachmentAccept"
            @change="selectClosureRefFile($event)"
          />
          <b-form-text v-if="canUploadClosureRefImages" :class="isDarkMode ? 'text-light' : 'text-muted'">
            Images and PDF files are allowed. Files upload when you submit the form.
          </b-form-text>
        </b-form-group>
        <div
          v-if="showClosureRefImagesSection && (savedClosureRefImages.length || pendingClosureRefImages.length)"
          class="d-flex flex-wrap mt-2"
        >
          <div
            v-for="url in savedClosureRefImages"
            :key="`saved-closure-${url}`"
            class="qms-attachment-preview mr-2 mb-2"
          >

<img
  v-if="isImageAttachmentUrl(url)"
  :src="url"
  alt="Role attachment"
  class="qms-clickable-image"
  @click="openImagePreview(url)"
/>
            <div v-else class="qms-attachment-file" :class="{ 'qms-attachment-file--dark': isDarkMode }">
              <font-awesome-icon :icon="['fas', 'file-pdf']" class="text-danger mr-1" />
              <span class="qms-attachment-file__name">{{ getAttachmentFileName(url) }}</span>
            </div>
            <b-button
              type="button"
              size="sm"
              variant="outline-primary"
              class="qms-attachment-preview__download box-shadow2"
              @click="downloadAttachment(url)"
            >
              <font-awesome-icon :icon="['fas', 'download']" />
            </b-button>
          </div>
          <div
            v-for="item in pendingClosureRefImages"
            :key="item.id"
            class="qms-attachment-preview mr-2 mb-2"
          >

<img
  v-if="isImageFile(item.file)"
  :src="item.preview"
  :alt="item.file.name"
  class="qms-clickable-image"
  @click="openImagePreview(item.preview)"
/>
            <div v-else class="qms-attachment-file" :class="{ 'qms-attachment-file--dark': isDarkMode }">
              <font-awesome-icon :icon="['fas', 'file-pdf']" class="text-danger mr-1" />
              <span class="qms-attachment-file__name">{{ item.file.name }}</span>
            </div>
            <b-button
              type="button"
              size="sm"
              variant="outline-primary"
              class="qms-attachment-preview__download box-shadow2"
              @click="downloadPendingAttachment(item)"
            >
              <font-awesome-icon :icon="['fas', 'download']" />
            </b-button>
            <b-button
              v-if="canUploadClosureRefImages"
              type="button"
              size="sm"
              variant="danger"
              class="qms-attachment-preview__remove box-shadow2"
              @click="removePendingClosureRefFile(item.id)"
            >
              <font-awesome-icon :icon="['fas', 'trash']" />
            </b-button>
          </div>
        </div>
        <b-form-group v-if="!isGuestNewForm" label-for="qms-closure-remarks">
          <template #label>
            <span>Closure remarks</span>
            <span v-if="closureRemarksRequired" class="text-danger">*</span>
          </template>
          <b-form-select
            v-if="formType === 'update'"
            id="qms-quick-replies"
            v-model="selectedQuickReply"
            :options="quickReplyOptions"
            :class="[inputClass, 'mb-2']"
            :disabled="isClosed || isGuestReadOnlyEdit"
            @change="onQuickReplySelect"
          >
            <template #first>
              <b-form-select-option value="">Suggestions — select a closure remark</b-form-select-option>
            </template>
          </b-form-select>
          <b-form-textarea
            id="qms-closure-remarks"
            v-model="form.closureRemarks"
            rows="4"
            :class="textareaClass"
            :disabled="isClosed || isGuestReadOnlyEdit"
            :required="closureRemarksRequired"
            :placeholder="closureRemarksPlaceholder"
          />
          <b-form-text v-if="isClosed" id="qms-closed-help" :class="isDarkMode ? 'text-light' : 'text-muted'">
            This query is closed; closure remarks cannot be changed.
          </b-form-text>
        </b-form-group>
        <b-form-group>
          <b-row class="mt-2">
            <b-col class="text-right">
              <b-button
                type="submit"
                size="sm"
                :variant="isDarkMode ? 'outline-success' : 'success'"
                class="box-shadow2"
                :disabled="isUpdateSubmitDisabled || isClosed || isGuestReadOnlyEdit || isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner spinner-white mr-1"></span>
                <font-awesome-icon v-else :icon="['fas', 'save']" class="mr-1" />{{ submitLabel }}
              </b-button>
              <b-button
                type="button"
                size="sm"
                class="ml-2 box-shadow2"
                :variant="isDarkMode ? 'outline-light' : 'outline-secondary'"
                @click="onCancel"
              >
                <font-awesome-icon :icon="['fas', 'long-arrow-alt-left']" class="mr-1" />Cancel
              </b-button>
            </b-col>
          </b-row>
        </b-form-group>
      </b-form>
    </b-overlay>
    <b-modal
  v-model="showImagePreview"
  hide-footer
  centered
  size="xl"
  title="Attachment Preview"
>
  <img
    v-if="previewImage"
    :src="previewImage"
    class="qms-preview-image"
    alt="Preview"
  />
</b-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import dropdownService from '@/services/dropdownService';
import {
  QMS_ATTACHMENT_ACCEPT,
  downloadQmsAttachment,
  getQmsAttachmentFileName,
  isAllowedQmsAttachmentFile,
  isQmsImageUrl
} from '@/services/qmsService';
import {
  QMS_MOBILE_STORAGE_KEY,
  hasQmsUserData,
  readQmsUserFromStorage,
  saveQmsUserToStorage
} from '@/helper/qmsUserSession';

/** Consumer dropdown key: grouped categories (Payroll, Comp & Benefit, In-store) and query-type options. */
const HR_DROPDOWN_KEY = 'hr_management';

export default {
  name: 'QmsFormBox',
  components: {
    Multiselect
  },
  props: {
    formType: {
      type: String,
      required: true
    },
    initial: {
      type: Object,
      default: null
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /** From API on edit: when true, closure remarks and update are read-only. */
      isClosed: false,
      loadingDropdowns: false,
      showImagePreview: false,
previewImage: '',
      submittingImages: false,
      qmsUserIdentityLocked: false,
      savedEmployeeRefImages: [],
      pendingEmployeeRefImages: [],
      savedClosureRefImages: [],
      pendingClosureRefImages: [],
      hrCategories: [],
      selectedQuickReply: '',
      storeIdSelect: {
        value: '',
        text: ''
      },
      form: {
        employeeCode: '',
        storeCode: '',
        employeeName: '',
        employeeMobile: '',
        department: '',
        queryType: '',
        queryDescription: '',
        closureRemarks: ''
      }
    };
  },
  beforeDestroy() {
    this.revokePendingPreviews();
  },
  computed: {
    ...mapState('darkMode', ['isDarkMode']),
    ...mapState('store', ['storeList', 'loading']),
    ...mapState('qms', ['qmsUserFromHono', 'responseTemplates']),
    ...mapGetters('auth', ['isLoggedIn', 'isQmsPayrollClosureRole']),
    isPayrollClosureOnlyMode() {
      return this.formType === 'update' && this.isQmsPayrollClosureRole && this.isLoggedIn();
    },
    showEmployeeRefImagesSection() {
      return this.isGuestNewForm || this.formType === 'update';
    },
    canUploadEmployeeRefImages() {
      return this.isGuestNewForm;
    },
    showClosureRefImagesSection() {
      return (
        this.formType === 'update' &&
        this.isLoggedIn() &&
        this.isQmsPayrollClosureRole &&
        !this.isQmsOtpGuestSession
      );
    },
    canUploadClosureRefImages() {
      return this.showClosureRefImagesSection && !this.isClosed;
    },
    isQmsOtpGuestSession() {
      if (typeof sessionStorage === 'undefined') {
        return false;
      }
      return sessionStorage.getItem('qmsOtpVerified') === 'true' && !this.isLoggedIn();
    },
    isGuestReadOnlyEdit() {
      return this.formType === 'update' && this.isQmsOtpGuestSession;
    },
    isGuestNewForm() {
      return this.formType === 'new' && this.isQmsOtpGuestSession;
    },
    isQmsUserIdentityLocked() {
      return this.formType === 'new' && this.qmsUserIdentityLocked;
    },
    storeOptions() {
      return (this.storeList || []).map(store => ({
        text: store.storeid,
        value: store.storeid
      }));
    },
    inputClass() {
      return this.isDarkMode ? 'box-shadow3 bg-dark text-light placeholder-light' : 'box-shadow3 bg-light text-dark';
    },
    textareaClass() {
      return this.isDarkMode
        ? 'box-shadow3 bg-dark text-light placeholder-light'
        : 'box-shadow3 text-dark bg-light';
    },
    submitLabel() {
      return this.formType === 'new' ? 'Submit' : 'Update';
    },
    /** Closure remarks are required when saving an existing query that is not already closed. */
    closureRemarksRequired() {
      return this.formType === 'update' && !this.isClosed;
    },
    trimmedClosureRemarks() {
      return String(this.form.closureRemarks || '').trim();
    },
    isUpdateSubmitDisabled() {
      if (this.formType !== 'update' || this.isClosed) {
        return false;
      }
      return !this.trimmedClosureRemarks;
    },
    isSubmitting() {
      return this.submittingImages || this.submitting;
    },
    closureRemarksPlaceholder() {
      if (this.isClosed) {
        return '';
      }
      return this.formType === 'update'
        ? 'Enter closure remarks (required to update)'
        : 'Add closure notes (optional)';
    },
    quickReplyOptions() {
      return (this.responseTemplates || [])
        .filter(template => template && template.isActive !== false && template.message)
        .map(template => ({ value: template.message, text: template.message }));
    },
    departmentSelectOptions() {
      return this.hrCategories
        .filter(c => c && (c.isActive === undefined || c.isActive !== false))
        .map(c => ({ value: c.label, text: c.label }));
    },
    queryTypeSelectOptions() {
      if (!this.form.department) return [];
      const cat = this.hrCategories.find(c => c && c.label === this.form.department);
      if (!cat || !Array.isArray(cat.options)) return [];
      const options = cat.options
        .filter(o => o && o.isActive !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(o => ({ value: o.value, text: o.label }));
      // Keep fetched legacy values visible during edit, even if not in active dropdown config.
      if (this.form.queryType && !options.some(o => o.value === this.form.queryType)) {
        options.unshift({ value: this.form.queryType, text: this.form.queryType });
      }
      return options;
    },
    attachmentAccept() {
      return QMS_ATTACHMENT_ACCEPT;
    }
  },
  watch: {
    initial: {
      deep: true,
      handler(val) {
        this.applyInitial(val);
      }
    },
    storeIdSelect: {
      deep: true,
      handler(val) {
        if (val && val.value != null && val.value !== '') {
          this.form.storeCode = String(val.value);
        }
      }
    }
  },
  async mounted() {
    this.listStore({ router: this.$router });
    this.loadDropdowns();
    if (this.formType === 'update' && !this.isQmsOtpGuestSession) {
      await this.listResponseTemplates({ router: this.$router }).catch(() => {});
    }
    this.resetForm();
    this.applyInitial(this.initial);
    await this.hydrateQmsUserDefaults();
  },
  methods: {
    ...mapActions('store', ['listStore']),
    ...mapActions('qms', ['getOneQmsUserFromHono', 'uploadDocument', 'listResponseTemplates']),
    mapAttachmentUrls(items) {
      return (items || [])
        .map(item => {
          if (typeof item === 'string') {
            return item.split('?')[0];
          }
          return item && item.url ? String(item.url).split('?')[0] : '';
        })
        .filter(Boolean);
    },
    openImagePreview(url) {
  if (!url) return;

  this.previewImage = url;
  this.showImagePreview = true;
},
    isImageFile(file) {
      if (!file || !file.type) {
        return false;
      }
      return file.type.startsWith('image/');
    },
    isImageAttachmentUrl(url) {
      return isQmsImageUrl(url);
    },
    getAttachmentFileName(url) {
      return getQmsAttachmentFileName(url);
    },
    downloadAttachment(url) {
      return downloadQmsAttachment(url);
    },
    downloadPendingAttachment(item) {
      if (!item || !item.file) {
        return;
      }
      const blobUrl = item.preview || URL.createObjectURL(item.file);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = item.file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (!item.preview) {
        URL.revokeObjectURL(blobUrl);
      }
    },
    revokePendingPreviews() {
      [...this.pendingEmployeeRefImages, ...this.pendingClosureRefImages].forEach(item => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
    },
    selectRefImageFile(event, pendingListName) {
      const canUpload =
        pendingListName === 'pendingEmployeeRefImages'
          ? this.canUploadEmployeeRefImages
          : this.canUploadClosureRefImages;
      if (!canUpload || !event.target.files || !event.target.files.length) {
        return;
      }

      const files = Array.from(event.target.files);
      const invalidFile = files.find(file => !isAllowedQmsAttachmentFile(file));
      if (invalidFile) {
        event.target.value = '';
        this.$store.dispatch(
          'alert/error',
          {
            showType: 'toast',
            position: 'center',
            title: '',
            text: 'Only image and PDF files are allowed.'
          },
          { root: true }
        );
        return;
      }

      files.forEach(file => {
        this[pendingListName].push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          preview: this.isImageFile(file) ? URL.createObjectURL(file) : null,
          fileName: `${Date.now()}-${file.name}`
        });
      });
      event.target.value = '';
    },
    selectEmployeeRefFile(event) {
      this.selectRefImageFile(event, 'pendingEmployeeRefImages');
    },
    selectClosureRefFile(event) {
      this.selectRefImageFile(event, 'pendingClosureRefImages');
    },
    removePendingRefFile(pendingListName, id) {
      const index = this[pendingListName].findIndex(item => item.id === id);
      if (index === -1) {
        return;
      }
      const [removed] = this[pendingListName].splice(index, 1);
      if (removed && removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
    },
    removePendingEmployeeRefFile(id) {
      this.removePendingRefFile('pendingEmployeeRefImages', id);
    },
    removePendingClosureRefFile(id) {
      this.removePendingRefFile('pendingClosureRefImages', id);
    },
    async uploadPendingImages(pendingList) {
      const urls = [];
      for (let i = 0; i < pendingList.length; i++) {
        const item = pendingList[i];
        const url = await this.uploadDocument({ file: item.file, fileName: item.fileName }, {}, this.$router);
        urls.push(url);
      }
      return urls;
    },
    resetForm() {
      this.isClosed = false;
      this.qmsUserIdentityLocked = false;
      this.revokePendingPreviews();
      this.pendingEmployeeRefImages = [];
      this.pendingClosureRefImages = [];
      this.savedEmployeeRefImages = [];
      this.savedClosureRefImages = [];
      this.selectedQuickReply = '';
      this.form = {
        employeeCode: '',
        storeCode: '',
        employeeName: '',
        employeeMobile: '',
        department: '',
        queryType: '',
        queryDescription: '',
        closureRemarks: ''
      };
      this.storeIdSelect = { value: '', text: '' };
    },
    onDepartmentChange() {
      this.form.queryType = '';
    },
    onQuickReplySelect(reply) {
      if (!reply) {
        return;
      }
      this.form.closureRemarks = reply;
      this.selectedQuickReply = '';
    },
    applyQmsUserDefaults(user) {
      if (!user) {
        return;
      }

      this.form.employeeCode = user.employeeCode || this.form.employeeCode;
      this.form.storeCode = user.storeCode || this.form.storeCode;
      if (this.form.storeCode) {
        this.storeIdSelect = { value: this.form.storeCode, text: this.form.storeCode };
      }
      this.form.employeeName = user.employeeName || this.form.employeeName;
      this.form.employeeMobile = user.employeeMobile || this.form.employeeMobile;
      this.qmsUserIdentityLocked = true;
    },
    async hydrateQmsUserDefaults() {
      if (this.formType !== 'new') {
        return;
      }

      const savedUser = readQmsUserFromStorage();
      if (hasQmsUserData(savedUser)) {
        this.applyQmsUserDefaults(savedUser);
        return;
      }

      const savedMobile = localStorage.getItem(QMS_MOBILE_STORAGE_KEY);
      if (!savedMobile) {
        return;
      }

      try {
        const user = await this.getOneQmsUserFromHono({
          id: savedMobile,
          router: this.$router,
          silent: true
        });
        if (!hasQmsUserData(user)) {
          return;
        }
        const normalizedUser = saveQmsUserToStorage(user);
        this.applyQmsUserDefaults(normalizedUser);
      } catch (_e) {
        // Keep the form editable when user details are unavailable.
      }
    },
    async loadDropdowns() {
      this.loadingDropdowns = true;
      try {
        const doc = await dropdownService.getByKey({ key: HR_DROPDOWN_KEY }).catch(() => null);
        this.hrCategories = (doc && doc.categories) || [];
        this.syncQueryTypeWithDropdown();
      } finally {
        this.loadingDropdowns = false;
      }
    },
    syncQueryTypeWithDropdown() {
      if (!this.form.department || !this.form.queryType) return;
      const cat = this.hrCategories.find(c => c && c.label === this.form.department);
      if (!cat || !Array.isArray(cat.options)) return;
      const options = cat.options.filter(o => o && o.isActive !== false);
      const hasValueMatch = options.some(o => o.value === this.form.queryType);
      if (hasValueMatch) return;
      const byLabel = options.find(o => o.label === this.form.queryType);
      if (byLabel && byLabel.value) {
        this.form.queryType = byLabel.value;
      }
    },
    applyInitial(val) {
      if (!val) {
        this.resetForm();
        return;
      }
      this.form.employeeCode = val.employeeCode || '';
      this.form.storeCode = val.storeCode || '';
      const code = this.form.storeCode;
      this.storeIdSelect = code ? { value: code, text: code } : { value: '', text: '' };
      this.form.employeeName = val.employeeName || '';
      this.form.employeeMobile = val.employeeMobile || '';
      this.form.department = val.department || '';
      this.form.queryType = val.queryType || '';
      this.syncQueryTypeWithDropdown();
      this.form.queryDescription = val.queryDescription || '';
      this.savedEmployeeRefImages = this.mapAttachmentUrls(val.employeeRefImages);
      this.savedClosureRefImages = this.mapAttachmentUrls(val.closureRefImages);
      this.pendingEmployeeRefImages = [];
      this.pendingClosureRefImages = [];
      this.form.closureRemarks = val.closureRemarks != null ? String(val.closureRemarks) : '';
      const closedRaw = val.isClosed != null ? val.isClosed : val.is_closed;
      this.isClosed =
        closedRaw === true ||
        closedRaw === 'true' ||
        closedRaw === 1 ||
        closedRaw === '1';
    },
    async onSubmit() {
      if (this.isSubmitting) {
        return;
      }
      if (this.isGuestReadOnlyEdit || (this.formType === 'update' && this.isClosed)) {
        return;
      }
      if (this.formType === 'update' && !this.trimmedClosureRemarks) {
        return;
      }

      this.submittingImages = true;
      try {
        const uploadedEmployeeUrls = this.canUploadEmployeeRefImages
          ? await this.uploadPendingImages(this.pendingEmployeeRefImages)
          : [];
        const uploadedClosureUrls = this.canUploadClosureRefImages
          ? await this.uploadPendingImages(this.pendingClosureRefImages)
          : [];

        const payload = {
          isClosed: this.isClosed === true,
          employeeRefImages: [...this.savedEmployeeRefImages, ...uploadedEmployeeUrls],
          closureRefImages: [...this.savedClosureRefImages, ...uploadedClosureUrls],
          employeeCode: String(this.form.employeeCode).trim(),
          storeCode: String(this.form.storeCode).trim(),
          employeeName: String(this.form.employeeName).trim(),
          employeeMobile: String(this.form.employeeMobile).trim(),
          department: String(this.form.department).trim(),
          queryType: String(this.form.queryType).trim(),
          queryDescription: String(this.form.queryDescription).trim()
        };
        if (this.formType === 'update') {
          payload.closureRemarks = this.trimmedClosureRemarks;
        }
        localStorage.setItem(QMS_MOBILE_STORAGE_KEY, payload.employeeMobile);
        if (this.formType === 'new') {
          this.$emit('create', { payload });
        } else {
          this.$emit('update', { payload });
        }
      } catch (_e) {
        // Upload errors are surfaced by the store action.
      } finally {
        this.submittingImages = false;
      }
    },
    onCancel() {
      this.$router.push('/qms');
    },
    goToDropdownsPage() {
      this.$router.push('/dropdowns');
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.qms-store-select-dark {
  color: #f8f9fa;
}

.qms-store-select-dark ::v-deep .multiselect__tags,
.qms-store-select-dark ::v-deep .multiselect__input,
.qms-store-select-dark ::v-deep .multiselect__single {
  background-color: #343a40 !important;
  color: #f8f9fa !important;
  border-color: #6c757d !important;
}

.qms-store-select-dark ::v-deep .multiselect__content-wrapper,
.qms-store-select-dark ::v-deep .multiselect__content,
.qms-store-select-dark ::v-deep .multiselect__option {
  background-color: #343a40 !important;
  color: #f8f9fa !important;
}

.qms-store-select-dark ::v-deep .multiselect__select:before,
.qms-store-select-dark ::v-deep .multiselect__placeholder {
  color: #f8f9fa !important;
}

.qms-attachment-preview {
  position: relative;
  width: 140px;
  min-height: 120px;
}

.qms-attachment-preview img {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.qms-attachment-file {
  display: flex;
  align-items: center;
  width: 120px;
  min-height: 80px;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  word-break: break-word;
}

.qms-attachment-file--dark {
  border-color: #6c757d;
  background: #343a40;
}

.qms-attachment-file__name {
  font-size: 0.75rem;
  line-height: 1.2;
}

.qms-attachment-preview__download {
  margin-top: 0.35rem;
  padding: 0.15rem 0.45rem;
  line-height: 1;
}

.qms-attachment-preview__remove {
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 0.15rem 0.35rem;
  line-height: 1;
}
.qms-clickable-image {
  cursor: pointer;
}

.qms-preview-image {
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
</style>
